<template>
  <div :key="$store.getters['resource/currentResource'].key">
    <loader v-if="loading"/>

    <!-- Main fields -->
    <b-row class="align-items-start">
      <!-- Title -->
      <b-col md="4">
        <b-input-group class="flex-column">
          <label class="d-block">{{ $t('form.title')}}
            <span class="text-danger">{{$t('form.required_star') }}</span>
          </label>
          <b-form-input
            v-model.trim="title"
            name="Title"
            class="mb-4 rounded"
          >
          </b-form-input>
        </b-input-group>
        <span class="text-danger position-absolute error"
              v-if="!$v.title.required && $v.title.$dirty">
            {{$t('form.required_field')}}
          </span>
        <span class="text-danger position-absolute error"
              v-if="!$v.title.maxLength">
            {{$t('errors.error_must_have') + " " + $v.title.$params.maxLength.max + " " + $t('errors.error_letters')}}
        </span>
      </b-col>

      <!-- Code -->
      <b-col md="4">
        <div>
          <label class="d-block">{{ $t('form.code')}}
          </label>
          <b-input-group class="d-flex flex-row flex-nowrap mb-4 ">
            <b-form-input
              class="rounded-left"
              v-model.trim="code"
              name="Code"></b-form-input>
            <b-btn
              @click="copyDataToClipboard(code, true)"
              :id="`copyButton-${code}`"
              variant="light"
              :disabled="!code"
              size="sm"
              class="input-height rounded-right mr-3">
              <i class="far fa-copy fa-2x text-muted"></i>
            </b-btn>
            <b-tooltip
              :target="`copyButton-${code}`"
              triggers="focus"
              :title="$t('invite.invite_code_copied')"
              placement="topright"
              :delay="{show: 0, hide: 800}"
            ></b-tooltip>
            <b-button
              style="width: 95px"
              :block="true"
              size="sm"
              @click="generateCode"
            >
              {{$t('button.randomize')}}
            </b-button>
          </b-input-group>
        </div>
      </b-col>

      <!-- Code URL -->
      <b-col md="4">
        <div>
          <label class="d-block">{{ $t('form.invite_link') }}
          </label>
          <b-input-group class="d-flex flex-row flex-nowrap mb-4 ">
            <b-form-input
                    class="rounded-left"
                    :value="inviteCodeFullURL(code)"
                    :disabled="true"
                    name="Code"></b-form-input>
            <b-btn
                    @click="copyDataToClipboard(code)"
                    :id="`copyButton-link-${code}`"
                    variant="light"
                    :disabled="!code"
                    size="sm"
                    class="input-height rounded-right mr-3">
              <i class="far fa-copy fa-2x text-muted"></i>
            </b-btn>
            <b-tooltip
                    :target="`copyButton-link-${code}`"
                    triggers="focus"
                    :title="$t('invite.invite_copied')"
                    placement="topright"
                    :delay="{show: 0, hide: 800}"
            ></b-tooltip>
          </b-input-group>
        </div>
      </b-col>

      <!-- Availability Dates -->
      <b-col md="4">
        <div class="mb-4">
          <label class="d-block">{{ $t('form.available')}}
          </label>
          <DatePickerInput
              v-model="available"
              is-range
          />
        </div>
      </b-col>

      <!-- Activations limit-->
      <b-col md="4">
        <b-input-group class="flex-column w-50 d-inline-block">
          <label class="d-block">{{$t('invite.activations_limit')}}</label>
          <b-form-input
            min="1"
            v-model="activationsLimit"
            class="mb-4 rounded"
            type="number"
            :disabled="unlimited"
          >
          </b-form-input>

        </b-input-group>

        <!-- Unlimited checkbox-->
        <div class="mr-1 ml-1 d-inline-block w-25">

          <b-form-checkbox
            id="unlimited_checkbox"
            class="form-check-input ml-3"
            v-model="unlimited"
          >
            {{$t('invite.unlimited')}}
          </b-form-checkbox>
        </div>
      </b-col>

      <!-- Group choosing -->
      <b-col md="4">
        <div v-if="!loading" class="position-relative mb-4">
          <span
            v-if="!!team"
            @click="team = null"
            class="text-muted
            top-right
            position-absolute
            pointer
            font-weight-light"
          >{{$t('button.clear')}}</span>
          <label class="d-block">
            {{$t('groups.group_subscription')}}
          </label>
          <multiselect
            v-model="team"
            :loading="isSelectLoading.team"
            deselectLabel="X"
            selectLabel=""
            selectedLabel=""
            :multiple="false"
            label="name"
            track-by="name"
            :options="teams"
            :custom-label="concatIdName"
            @search-change="value => {loadTeams('name', value)}"
            :searchable="true"
            :allow-empty="true"
            :disabled="!!plan.value || !!ticket.value">
            <template slot="noResult">
              {{$t('global.no_results')}}
            </template>

            <template slot="singleLabel" slot-scope="{ option }" v-if="team && team.id">
              {{'#' + option.id + ' ' + option.name }}
            </template>

          </multiselect>

        </div>
      </b-col>


      <!-- Plan-Subscription choosing -->
      <b-col md="4">
        <div class="position-relative mb-4 " v-if="!loading">
          <span
            v-if="plan.value"
            @click="removeSubscription"
            class="text-muted
            top-right
            position-absolute
            pointer
            font-weight-light"
          >{{$t('button.clear')}}</span>
          <group-select
            :model="plan"
            :input="planInput"
            :disabled="!!team || !!ticket.value"
            @select="selectSubscription"
            @remove="removeSubscription"

          />
        </div>
      </b-col>

      <!-- Tickets choosing -->
      <b-col md="4">
        <div class="position-relative mb-4 " v-if="!loading">
          <span
              v-if="ticket.value"
              @click="removeTicket"
              class="text-muted top-right position-absolute pointer font-weight-light"
          >{{$t('button.clear')}}</span>
          <group-select
              :model="ticket"
              :input="ticketInput"
              :disabled="!!team || !!plan.value"
              @select="selectTicket"
              @remove="removeTicket"

          />
        </div>
      </b-col>

      <!-- Subscription End Date -->
      <b-col md="4" v-if="plan.value">
        <div class="mb-4">
          <label class="d-block">{{ $t('invite.end_date')}}
          </label>
          <DatePickerInput
              v-model="endDate"
              :disabled="true"
          />
        </div>
      </b-col>

      <!-- Description -->
      <b-col md="4">
        <div class="mb-4">
          <label class="d-block">{{ $t('form.description')}}
          </label>
          <textarea
            :rows="2"
            v-model="description"
            class="form-control"
          >

          </textarea>
        </div>
      </b-col>


    </b-row>

    <!-- Action buttons -->

    <form-buttons @submit="debounceSubmit"></form-buttons>

  </div>
</template>
<script>
  import {required, maxLength} from "vuelidate/lib/validators";
  import CodeRain from "coderain";
  import i18n from '@/utils/i18n';
  import {inviteMethods} from '@/mixins/invite';
  import {isObject} from "lodash";
  import DatePickerInput from "../../components/forms/fields/datePickerInput";

  const crTemplate = "##################";
  const cr = new CodeRain(crTemplate);
  const maxTitleLength = 128;

  let invite = {};

  export default {
    components: {DatePickerInput},
    props: {
      params: {
        id: {
          type: Number
        }
      }
    },
    mixins: [inviteMethods],
    data: () => {
      return {
        isSelectLoading: {},
        validate: false,
        loading: true,
        title: "",
        code: "",
        description: '',
        unlimited: false,

        plan: {},
        ticket: {},

        ticketInput: {
          "name": "subscriptions_id",
          "type": "select",
          "label": i18n.t('form.event_and_ticket'),
          placeholder: i18n.t('transactions.type_to_search'),
          searchable: true,
          "multiple": false,
          group: true,
          items_key: "tickets",
          "options_url": "ti-events?limit=100",
          filter_field: 'title',
          "main": false,
          "disabled": false
        },

        planInput: {
          "name": "subscriptions_id",
          "type": "select",
          "label": i18n.t('form.plan_subscription'),
          placeholder: i18n.t('transactions.type_to_search'),
          searchable: true,
          "multiple": false,
          group: true,
          "options_url": "plans?limit=100",
          filter_field: 'title',
          "main": false,
          "disabled": false
        },

        team: null,
        teams: [],

        available: {},
        endDate: null,
        activationsLimit: '1',
        debounceSubmit: () => {
        }
      }
    },
    validations: {
      title: {
        required,
        maxLength: maxLength(maxTitleLength)
      }
    },
    async created() {
      try {
        if (this.params && this.params.id) {
          let {data} = await this.loadInvite(this.params.id);
          invite = data;
        }
      } catch (e) {
        this.$router.replace({name: "404"});
      }

      try {
        await this.loadTeams();
      } catch (e) {
        this.$noty.error(i18n.t("errors.error_load_teams"));
      }

      this.prefillData();
      this.loading = false;
      this.debounceSubmit = _.debounce(this.submit, 1000);
    },
    methods: {
      selectTicket(params) {
        this.ticket = params.value
      },
      removeTicket() {
        this.ticket = {};
      },
      selectSubscription(params) {
        this.plan = params.value;
      },
      removeSubscription() {
        this.plan = {};
      },
      loadInvite(id) {
        return this.getData(`invites/${id}`, null, true);
      },
      loadTeams(filter_field, value) {
        this.$set(this.isSelectLoading, 'team', true);
        const params = {filter: {}};
        if (filter_field) {
          params.filter[filter_field] = value
        }
        try {
          this.getData("teams?limit=100", {params}).then(({data}) => {
            this.teams = data.items;
          }).then(() => {
            this.$set(this.isSelectLoading, 'team', false);
          })
        } catch (e) {
          console.log(e);
          this.$set(this.isSelectLoading, 'team', false);
        }
      },
      prefillData() {
        if (!this.params || !this.params.id) {
          return;
        }

        this.title = invite.title || "";
        this.code = invite.code || "";
        this.description = invite.description || "";
        this.team = invite.team ? invite.team : null;
        this.plan = invite.subscription ? {
          label: invite.subscription.title,
          value: invite.subscription.id
        } : {
          label: "",
          value: 0
        };
        this.ticket = invite.ti_event_ticket ? {
          label: invite.ti_event_ticket.title,
          value: invite.ti_event_ticket.id
        } : {
          label: "",
          value: 0
        }
        if (invite.start_date && invite.end_date) {
          this.available = {
            start: new Date(invite.start_date),
            end: new Date(invite.end_date)
          };
        }
        if (invite.subscription_end_date) {
          this.endDate = new Date(invite.subscription_end_date);
        }

        this.activationsLimit = invite.activations_limit ? invite.activations_limit.toString() : '1';
        this.unlimited = invite.activations_limit <= 0;


      },
      submit() {
        this.validate = true;
        this.$v.$touch();
        if (this.$v.title.$invalid) {
          return;
        }
        let params = {
          title: this.title,
          code: this.code,
          description: this.description,
          start_date: this.formatDate(this.available.start, "L"),
          end_date: this.formatDate(this.available.end, "L"),
          subscription_end_date: this.formatDate(this.endDate, "L"),
          activations_limit: this.unlimited ? 0 : parseInt(this.activationsLimit),
        };
        if (!!this.team) {
          params.team_id = this.team.id;
        } else if (!!this.plan.value) {
          params.subscription_id = this.plan.value;
        } else if (!!this.ticket.value) {
          params.ti_event_ticket_id = this.ticket.value;
        }
        this.$emit('save', params);
      },
      concatIdName({id, name}) {
        return `#${id} ${name}`
      },
      generateCode() {
        this.code = cr.next();
      }
    }
  }
</script>
